import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, ApiError } from '@/lib/api-client';
import { useSnackbarStore } from '@certara/common-react-components';

type StartFilesUploadResponse = {
  ResponseType: string;
  PartSize: number;
  PartUrls: string[];
  UploadId: string;
  FilePath: string;
  FileName: string;
  ErrorMessage?: string;
};

export interface FileUploadProgress {
  fileName: string;
  progress: number;
  status:
    | 'pending'
    | 'uploading'
    | 'completed'
    | 'failed'
    | 'validation-failed'
    | 'completed-with-warning';
  error?: string;
  warning?: string;
}

export interface ValidationFailure {
  fileName: string;
  path: string;
  error: string;
}

export interface ValidationWarning {
  fileName: string;
  path: string;
  warning: string;
}

export interface UseFileUploadOptions {
  companyId?: string;
  currentPath?: string;
  onProgress?: (progress: Map<string, FileUploadProgress>) => void;
  showSnackbars?: boolean;
}

export function useFileUpload(options: UseFileUploadOptions = {}) {
  const { companyId, currentPath = '', onProgress, showSnackbars = true } = options;
  const [uploadProgress, setUploadProgress] = useState<Map<string, FileUploadProgress>>(
    new Map()
  );
  const { addSnackbar } = useSnackbarStore();
  const queryClient = useQueryClient();

  const updateProgress = (
    fileName: string,
    updates:
      | Partial<FileUploadProgress>
      | ((prev: FileUploadProgress) => Partial<FileUploadProgress>)
  ) => {
    setUploadProgress((prev) => {
      const newMap = new Map(prev);
      const current = newMap.get(fileName) || {
        fileName,
        progress: 0,
        status: 'pending' as const,
      };
      const updatesToApply = typeof updates === 'function' ? updates(current) : updates;
      newMap.set(fileName, { ...current, ...updatesToApply });
      onProgress?.(newMap);
      return newMap;
    });
  };

  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const results = await Promise.allSettled(
        files.map(async (file) => {
          const relativePath =
            (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
            file.name;

          try {
            if (!companyId) {
              throw new Error('Company ID is required for uploads');
            }

            const companyIdInt = parseInt(companyId);

            updateProgress(relativePath, { status: 'uploading', progress: 0 });

            const filePath = currentPath
              ? `${currentPath.replace(/\/$/, '')}/${relativePath}`
              : relativePath;
            const locationPath = currentPath || '/';

            let startResponse: StartFilesUploadResponse[] | null;
            try {
              startResponse = await apiClient.postForm<StartFilesUploadResponse[]>(
                'api/Storage/StartFilesUpload',
                {
                  companyId: companyIdInt,
                  files: [{ path: filePath, size: file.size }],
                  currentLocationPath: locationPath,
                }
              );
            } catch (error) {
              if (error instanceof ApiError) {
                throw new Error(
                  `Failed to start upload (${error.status}): ${error.statusText}`
                );
              }
              throw new Error(
                'Failed to start upload. Please check your network connection.'
              );
            }

            if (!startResponse) {
              throw new Error('Server returned empty response when starting upload');
            }

            const uploadInfo = startResponse[0];

            if (!uploadInfo) {
              throw new Error('Server returned no upload info');
            }

            if (uploadInfo.ResponseType?.toLowerCase() !== 'success') {
              throw new Error(
                uploadInfo.ErrorMessage ||
                  `Upload initialization failed: ${uploadInfo.ResponseType}`
              );
            }

            const partSize = uploadInfo.PartSize;
            const partUrls = uploadInfo.PartUrls;
            const eTags: string[] = [];

            if (!partUrls || partUrls.length === 0) {
              throw new Error('No upload URLs received from server');
            }

            let backendWarning: string | undefined;
            const expectedFileName = filePath.split('/').pop();

            if (
              uploadInfo.FileName &&
              expectedFileName &&
              uploadInfo.FileName !== expectedFileName
            ) {
              backendWarning = `File was renamed by server to avoid collision: ${expectedFileName} → ${uploadInfo.FileName}`;
            } else if (uploadInfo.FilePath) {
              const backendFilename = uploadInfo.FilePath.split('/').pop();
              if (
                backendFilename &&
                expectedFileName &&
                backendFilename !== expectedFileName
              ) {
                backendWarning = `File was renamed by server to avoid collision: ${expectedFileName} → ${backendFilename}`;
              }
            }

            for (let i = 0; i < partUrls.length; i++) {
              const start = i * partSize;
              const end = Math.min(start + partSize, file.size);
              const blob = file.slice(start, end);

              let uploadResponse: Response;
              try {
                // S3 presigned PUT — plain fetch, NO Authorization header (the
                // URL is signed via query params; S3 rejects requests that also
                // present a bearer).
                uploadResponse = await fetch(partUrls[i], {
                  method: 'PUT',
                  body: blob,
                });
              } catch (error) {
                throw new Error(
                  `Network error uploading part ${i + 1}/${partUrls.length}. Please check your connection.`
                );
              }

              if (!uploadResponse.ok) {
                throw new Error(
                  `Failed to upload part ${i + 1}/${partUrls.length}: ${uploadResponse.status} ${uploadResponse.statusText}`
                );
              }

              const etag = uploadResponse.headers.get('ETag')?.replace(/"/g, '');
              if (etag) eTags.push(etag);

              const progress = ((i + 1) / partUrls.length) * 100;
              updateProgress(relativePath, { progress });
            }

            try {
              await apiClient.postForm('api/Storage/CompleteFileUpload', {
                companyId: companyIdInt,
                uploadId: uploadInfo.UploadId,
                path: uploadInfo.FilePath,
                partETags: eTags,
              });
            } catch (error) {
              if (error instanceof ApiError) {
                throw new Error(
                  `Failed to complete upload (${error.status}): ${error.statusText}`
                );
              }
              throw new Error('Failed to complete upload.');
            }

            updateProgress(relativePath, (prev) => {
              const combinedWarning = [prev.warning, backendWarning]
                .filter(Boolean)
                .join('; ');
              return {
                ...prev,
                status: combinedWarning ? 'completed-with-warning' : 'completed',
                progress: 100,
                warning: combinedWarning || undefined,
              };
            });

            return { fileName: relativePath, success: true };
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Upload failed';
            updateProgress(relativePath, {
              status: 'failed',
              error: errorMessage,
              progress: 0,
            });
            return { fileName: relativePath, success: false, error: errorMessage };
          }
        })
      );

      return results;
    },

    onSuccess: (results) => {
      const successful = results.filter(
        (r) => r.status === 'fulfilled' && r.value.success
      ).length;
      const failed = results.length - successful;

      if (showSnackbars) {
        if (failed > 0 && successful > 0) {
          addSnackbar(`Uploaded ${successful} of ${results.length} files`, 'warning');
        } else if (failed > 0) {
          const failedFiles = results
            .filter((r) => r.status === 'fulfilled' && !r.value.success)
            .map((r) => (r.status === 'fulfilled' ? r.value.fileName : 'unknown'))
            .join(', ');
          const errorDetails = results
            .filter((r) => r.status === 'fulfilled' && !r.value.success)
            .map((r) => (r.status === 'fulfilled' ? r.value.error : 'Unknown error'))
            .join('; ');
          addSnackbar(
            `Upload failed for ${failed} file(s): ${failedFiles}`,
            'error',
            errorDetails,
            true
          );
        } else {
          addSnackbar(`Successfully uploaded ${successful} file(s)`, 'success');
        }
      }

      if (successful > 0) {
        queryClient.invalidateQueries({
          queryKey: [
            'gridData',
            companyId ? parseInt(companyId) : undefined,
            currentPath,
          ],
        });
      }
    },

    onError: (error: unknown) => {
      if (showSnackbars) {
        const message = error instanceof Error ? error.message : 'Unknown error occurred';
        addSnackbar('Upload failed', 'error', message, true);
      }
    },
  });

  const uploadFiles = async (
    files: File[],
    validationFailures?: ValidationFailure[],
    validationWarnings?: ValidationWarning[]
  ) => {
    if (!files.length && !validationFailures?.length) return;

    validationFailures?.forEach((failure) => {
      updateProgress(failure.fileName, {
        status: 'validation-failed',
        progress: 0,
        error: failure.error,
      });
    });

    const warningMap = new Map(validationWarnings?.map((w) => [w.fileName, w.warning]));

    files.forEach((file) => {
      const relativePath =
        (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
      updateProgress(relativePath, {
        status: 'pending',
        progress: 0,
        warning: warningMap.get(relativePath),
      });
    });

    if (files.length > 0) {
      await uploadMutation.mutateAsync(files);
    }
  };

  const clearProgress = () => {
    setUploadProgress(new Map());
  };

  return {
    uploadFiles,
    uploadProgress,
    isUploading: uploadMutation.isPending,
    clearProgress,
  };
}
